import { useMemo } from 'react';
import { Badge, Card, Group, Skeleton, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCalendarEvent, IconFolders, IconSchool } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { FacilitatorSessionView, LeaderSessionView } from '@actschurch/ccms-sdk';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { toSDKDate } from '@/utils/dateTime';
import { UPCOMING_SESSION_COUNT } from '../constants';

/**
 * MySessionsList — one merged upcoming-sessions list across both roles.
 *
 * Care sessions (`LeaderSessionView`) and course sessions (`FacilitatorSessionView`) are
 * different DTOs but the same thing to the person reading the page: an appointment they
 * have to show up for. Normalising them here means a dual-role user gets a single
 * chronological list instead of two half-empty cards they have to mentally interleave.
 *
 * `kind` is surfaced as an icon + badge so the two sources stay distinguishable without
 * relying on colour alone.
 */
export interface NormalisedSession {
  id: string;
  title: string;
  meta?: string;
  when: number;
  whenDisplay: string;
  isToday: boolean;
  kind: 'care' | 'course';
  statusName?: string | null;
  statusColor?: string | null;
  to?: string;
}

export function normaliseLeaderSession(s: LeaderSessionView): NormalisedSession {
  return {
    id: `care-${s.sessionID}`,
    title: s.name ?? 'Care session',
    meta: [s.caseNames, s.locationName ?? (s.link ? 'Virtual' : null)].filter(Boolean).join(' · ') || undefined,
    when: s.sessionDateTime,
    whenDisplay: s.sessionDateDisplay ?? '—',
    isToday: Boolean(s.isToday),
    kind: 'care',
    statusName: s.statusName,
    statusColor: s.statusColor,
    to: '/Counselling/MyCases',
  };
}

export function normaliseFacilitatorSession(s: FacilitatorSessionView): NormalisedSession {
  return {
    id: `course-${s.registrationSessionID}`,
    title: s.name ?? 'Course session',
    meta: [s.courseName, s.registrationReference, `${s.participantCount} participants`]
      .filter(Boolean).join(' · ') || undefined,
    when: s.sessionDateTime,
    whenDisplay: s.sessionDateDisplay ?? '—',
    isToday: Boolean(s.isToday),
    kind: 'course',
    statusName: s.statusName,
    statusColor: s.statusColor,
    to: `/Grow/Enrolment/RegistrationDetails/${s.registrationID}`,
  };
}

export interface MySessionsListProps {
  sessions: NormalisedSession[];
  loading?: boolean;
  title?: string;
  subtitle?: string;
  limit?: number;
}

export default function MySessionsList({
  sessions,
  loading = false,
  title = 'Upcoming sessions',
  subtitle = 'Across your cases and courses',
  limit = UPCOMING_SESSION_COUNT,
}: MySessionsListProps) {
  const navigate = useNavigate();

  /**
   * Soonest first, and genuinely upcoming — anything already past belongs in the
   * attention band as overdue work, not in a schedule the user is meant to plan around.
   * "Today" is the lower bound rather than "now", so a session earlier today still shows.
   */
  const upcoming = useMemo(() => {
    const todayStart = Number(toSDKDate(new Date()));
    return sessions
      .filter((s) => s.when >= todayStart)
      .sort((a, b) => a.when - b.when)
      .slice(0, limit);
  }, [sessions, limit]);

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Text fw={600}>{title}</Text>
      <Text size="sm" c="dimmed" mb="md">{subtitle}</Text>

      {loading && sessions.length === 0 ? (
        <Stack gap="xs">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={44} radius="sm" />
          ))}
        </Stack>
      ) : upcoming.length === 0 ? (
        <Text size="sm" c="dimmed">Nothing scheduled.</Text>
      ) : (
        <Stack gap={4}>
          {upcoming.map((session) => (
            <UnstyledButton
              key={session.id}
              onClick={() => session.to && navigate(session.to)}
              style={{ borderRadius: 6 }}
            >
              <Group justify="space-between" wrap="nowrap" py={6} px={4}>
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon
                    size={32}
                    radius="md"
                    variant="light"
                    color={session.kind === 'care' ? 'indigo' : 'teal'}
                  >
                    {session.kind === 'care' ? <IconFolders size={16} /> : <IconSchool size={16} />}
                  </ThemeIcon>
                  <div style={{ minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={500} truncate>{session.title}</Text>
                      {session.isToday && (
                        <Badge size="xs" variant="filled" color="green">Today</Badge>
                      )}
                    </Group>
                    {session.meta && (
                      <Text size="xs" c="dimmed" truncate>{session.meta}</Text>
                    )}
                  </div>
                </Group>

                <Group gap="xs" wrap="nowrap">
                  <Group gap={4} wrap="nowrap">
                    <IconCalendarEvent size={13} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {session.whenDisplay}
                    </Text>
                  </Group>
                  {session.statusName && (
                    <Badge
                      size="sm"
                      variant="filled"
                      style={brandBadgeStyle(session.statusColor)}
                      styles={badgeStyles}
                    >
                      {session.statusName}
                    </Badge>
                  )}
                </Group>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </Card>
  );
}
